import { Suspense, lazy } from 'react';
import { Outlet, createBrowserRouter } from 'react-router-dom';
import { PageLoader, ToastProvider } from '@/components/ui';
import { AppLayout } from './AppLayout';
import { AdminLayout } from './AdminLayout';
import { RequireAuth } from './RequireAuth';
import { RequireAdmin } from './RequireAdmin';
import { RouteError } from './RouteError';

// Every page is a separate chunk. The 3D track view in particular pulls three.js,
// which must never be in the initial download.
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const GoogleCallback = lazy(() => import('@/pages/GoogleCallback'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Import = lazy(() => import('@/pages/Import'));
const Sessions = lazy(() => import('@/pages/Sessions'));
const SessionDetail = lazy(() => import('@/pages/SessionDetail'));
const Compare = lazy(() => import('@/pages/Compare'));
const Track3D = lazy(() => import('@/pages/Track3D'));
const Ranking = lazy(() => import('@/pages/Ranking'));
const Profile = lazy(() => import('@/pages/Profile'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const AdminOverview = lazy(() => import('@/pages/admin/AdminOverview'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('@/pages/admin/AdminUserDetail'));
const AdminSessions = lazy(() => import('@/pages/admin/AdminSessions'));
const AdminAudit = lazy(() => import('@/pages/admin/AdminAudit'));
const AdminLogins = lazy(() => import('@/pages/admin/AdminLogins'));

/** App-wide providers that must sit above the router outlet. */
function Root() {
  return (
    <ToastProvider>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </ToastProvider>
  );
}

/**
 * The full route table.
 *
 * Public: /login, /registrar, /entrar/google
 * Authenticated (AppLayout): /, /importar, /sessoes, /sessoes/:id, /comparar,
 *   /pista-3d/:lapId, /ranking, /perfil
 * Admin only (AdminLayout): /admin, /admin/usuarios, /admin/usuarios/:id,
 *   /admin/sessoes, /admin/auditoria, /admin/logins
 */
export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/registrar', element: <Register /> },
      // Where /api/auth/google/callback sends the browser once Google is done.
      { path: '/entrar/google', element: <GoogleCallback /> },

      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Dashboard /> },
              { path: 'importar', element: <Import /> },
              { path: 'sessoes', element: <Sessions /> },
              { path: 'sessoes/:id', element: <SessionDetail /> },
              { path: 'comparar', element: <Compare /> },
              { path: 'pista-3d/:lapId', element: <Track3D /> },
              { path: 'ranking', element: <Ranking /> },
              { path: 'perfil', element: <Profile /> },
            ],
          },
        ],
      },

      {
        element: <RequireAdmin />,
        children: [
          {
            path: 'admin',
            element: <AdminLayout />,
            children: [
              { index: true, element: <AdminOverview /> },
              { path: 'usuarios', element: <AdminUsers /> },
              { path: 'usuarios/:id', element: <AdminUserDetail /> },
              { path: 'sessoes', element: <AdminSessions /> },
              { path: 'auditoria', element: <AdminAudit /> },
              { path: 'logins', element: <AdminLogins /> },
            ],
          },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
]);

export default router;
